import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';

import { ItemsService } from '../../../../services/financeiro/item.service';
import { GrupoService } from '../../../../services/grupo/grupo.service';
import { ItemDePedidoConsultaDTO } from '../../../../models/pedidos/pedido.model';
import { environment } from '../../../../environments/environment';

/** Produto escolhido na grade, no formato que o pai precisa para a pendência. */
export interface ProdutoEscolhidoNaGrade {
  itemId: number;
  /** Codigo do cadastro — e o que a coluna COD. da tabela de itens exibe. */
  codigoItem?: number;
  /** Codigo de barras, para a coluna opcional da tabela de itens. */
  barras?: string;
  nome: string;
  valorUnitario: number;
}

/**
 * Diálogo de seleção de produtos para adicionar itens a um pedido existente.
 *
 * Reaproveita a lógica do grid de "Criar Pedido" (busca /item/buscar apenas
 * mercadorias com saldo, chips de grupo via /grupo/filtrarGrupo).
 *
 * A grade NÃO grava nada: cada clique apenas avisa o pai, que acumula a escolha
 * como pendência e a envia junto com o resto no lote confirmado pelo operador.
 * Antes cada clique virava um POST — e cada POST, uma comanda impressa.
 * O badge soma o que já está no pedido com o que está pendente no pai.
 */
@Component({
  selector: 'app-pedido-adicionar-item-dialog',
  templateUrl: './pedido-adicionar-item-dialog.component.html',
  styleUrls: ['./pedido-adicionar-item-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PedidoAdicionarItemDialogComponent implements OnChanges, OnDestroy {
  @Input() visivel = false;
  /** Itens atuais do pedido — usados para calcular o badge de quantidade. */
  @Input() itens: ItemDePedidoConsultaDTO[] = [];
  /**
   * Quantidades ainda não confirmadas, por id de item do catálogo. O dono do
   * estado é o pai; aqui elas só entram na conta do badge.
   */
  @Input() pendencias: Record<number, number> = {};

  // Configuração de moeda propagada pelo componente pai.
  @Input() moedaPadrao: 'BRL' | 'PYG' | 'USD' | 'ARS' = 'BRL';
  @Input() localeMoeda = 'pt-BR';
  @Input() digitsMoeda = '1.2-2';

  @Output() visivelChange = new EventEmitter<boolean>();
  /** Um clique num produto = uma unidade escolhida. Quem soma é o pai. */
  @Output() produtoEscolhido = new EventEmitter<ProdutoEscolhidoNaGrade>();

  env = environment;
  idEmpresa = '';

  // Grid de produtos.
  produtos: any[] = [];
  loading = false;
  paginaAtual = 0;
  tamanhoPagina = 12;
  totalElements = 0;
  produtoNaoEncontrado = false;
  private termoBusca = '';
  private buscaSubject = new Subject<void>();
  private imagensComErro = new Set<string>();

  // Chips de categoria + checkbox de saldo.
  grupos: any[] = [];
  categoriaSelecionada: any = { id: null, nome: 'Todas' };
  mostrarSemSaldo = false;

  // Quantidade base (do backend) por item do catálogo, derivada de `itens`.
  private baseQtdPorItem = new Map<number, number>();

  constructor(
    private itemsService: ItemsService,
    private grupoService: GrupoService,
    private translate: TranslateService,
    private cdr: ChangeDetectorRef,
  ) {
    this.buscaSubject.pipe(debounceTime(350)).subscribe(() => {
      this.paginaAtual = 0;
      this.carregarProdutos();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['itens']) {
      this.recomputarBase();
    }
    // Ao abrir o diálogo, (re)carrega grid e categorias.
    if (changes['visivel'] && this.visivel && !changes['visivel'].previousValue) {
      this.idEmpresa = sessionStorage.getItem('id') || '';
      this.categoriaSelecionada = { id: null, nome: this.translate.instant('criar_pedido.todas_categorias') };
      this.carregarGrupos();
      this.carregarProdutos();
    }
  }

  ngOnDestroy(): void {
    this.buscaSubject.complete();
  }

  onHide(): void {
    this.visivelChange.emit(false);
  }

  // ============================================================
  // Badge de quantidade já no pedido
  // ============================================================

  private recomputarBase(): void {
    this.baseQtdPorItem.clear();
    (this.itens || []).forEach(item => {
      const catalogoId = item.item?.id;
      if (catalogoId != null) {
        this.baseQtdPorItem.set(catalogoId, (this.baseQtdPorItem.get(catalogoId) || 0) + (item.quantidade || 0));
      }
    });
  }

  quantidadeNoPedido(produto: any): number {
    const id = produto?.id;
    if (id == null) return 0;
    return (this.baseQtdPorItem.get(id) || 0) + (this.pendencias?.[id] || 0);
  }

  // ============================================================
  // Grid de produtos (reaproveitado de Criar Pedido)
  // ============================================================

  carregarGrupos(): void {
    const labelTodas = this.translate.instant('criar_pedido.todas_categorias');
    this.grupoService.get('/grupo/filtrarGrupo', { status: true }).subscribe({
      next: (res: any) => {
        this.grupos = [{ id: null, nome: labelTodas }, ...(res?.content ?? [])];
        this.cdr.markForCheck();
      },
      error: () => {
        this.grupos = [{ id: null, nome: labelTodas }];
        this.cdr.markForCheck();
      },
    });
  }

  carregarProdutos(): void {
    this.loading = true;
    this.produtoNaoEncontrado = false;
    this.cdr.markForCheck();

    const termo = this.termoBusca.trim();
    const params: any = {
      page: this.paginaAtual,
      size: this.tamanhoPagina,
      sort: 'codigoItem,asc',
      apenasMercadoria: true,
      apenasComSaldo: !this.mostrarSemSaldo,
    };
    if (this.categoriaSelecionada?.id) {
      params.grupoId = this.categoriaSelecionada.id;
    }
    if (termo) {
      params.filter =
        `nome=${termo}&barras=${termo}&codigoItem=${termo}` +
        `&nomeGrupo=${termo}&nomeSubGrupo=${termo}`;
    }

    this.itemsService.get('/item/buscar', params).subscribe({
      next: (res: any) => {
        this.produtos = res?.content ?? [];
        this.totalElements = res?.totalElements ?? 0;
        this.produtoNaoEncontrado = this.produtos.length === 0;
        this.loading = false;
        this.cdr.markForCheck();
      },
      error: () => {
        this.produtos = [];
        this.totalElements = 0;
        this.produtoNaoEncontrado = true;
        this.loading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onPaginatorChange(event: any): void {
    this.paginaAtual = event.page ?? 0;
    this.tamanhoPagina = event.rows ?? this.tamanhoPagina;
    this.carregarProdutos();
  }

  selecionarCategoria(grupo: any): void {
    this.categoriaSelecionada = grupo;
    this.paginaAtual = 0;
    this.carregarProdutos();
  }

  onToggleSemSaldo(): void {
    this.paginaAtual = 0;
    this.carregarProdutos();
  }

  onInputChange(event: Event): void {
    this.termoBusca = (event.target as HTMLInputElement).value ?? '';
    this.buscaSubject.next();
  }

  onSearchEnter(ev: Event): void {
    this.termoBusca = ((ev.target as HTMLInputElement)?.value || '').trim();
    this.paginaAtual = 0;
    this.carregarProdutos();
  }

  indisponivel(p: any): boolean {
    return Number(p?.saldo ?? 0) <= 0;
  }

  podeClicar(p: any): boolean {
    return !this.indisponivel(p);
  }

  onImageError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.style.display = 'none';
    const produtoId = img.dataset['productId'];
    if (produtoId) {
      this.imagensComErro.add(produtoId);
    }
  }

  temErroImagem(produto: any): boolean {
    return this.imagensComErro.has(String(produto.id));
  }

  // ============================================================
  // Escolher item
  // ============================================================

  /** Emite a escolha ao pai — nada vai para a rede aqui. */
  adicionarProduto(produto: any): void {
    if (!produto?.id) return;
    this.produtoEscolhido.emit({
      itemId: produto.id,
      codigoItem: produto.codigoItem,
      barras: produto.barras,
      nome: produto.nome || produto.descricao || '-',
      valorUnitario: Number(produto.precoDeVenda ?? 0),
    });
    this.cdr.markForCheck();
  }
}
